import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import QRCode from 'qrcode';
import ModalShell from './ModalShell';
import {
  buildReceiptLines,
  copyReceiptText,
  downloadBlob,
  formatReceiptAmount,
  generateReceiptImageFromElement,
  generateReceiptPdfFromElement,
  resolveReceiptBranding,
  shareReceiptToMember,
  ENTREA_RECEIPT_COLORS,
} from '../utils/contributionReceipt';

/** Statuts considérés comme « non encore payés » pour facturation. */
export function isUnpaidContributionStatus(status) {
  const s = String(status || '').toLowerCase();
  return s === 'pending' || s === 'unpaid' || s === 'awaiting' || s === 'draft';
}

/**
 * Modal avis de cotisation(s) non payée(s) — même principe que le reçu (image / PDF / partage).
 */
export default function ContributionInvoiceModal({
  open,
  onClose,
  settings,
  orgName: orgNameProp,
  logoUrl: logoUrlProp,
  memberName,
  memberPhone = '',
  contributions = [],
  issuedAt,
  dueAt = '',
  localeFmt = 'fr-FR',
  onToast,
}) {
  const { t } = useTranslation('common');
  const [busy, setBusy] = useState('');
  const [qrDataUrl, setQrDataUrl] = useState('');
  const previewRef = useRef(null);

  const branding = useMemo(() => {
    const base = resolveReceiptBranding(settings);
    const warn = ENTREA_RECEIPT_COLORS.warn || '#d48b00';
    return {
      ...base,
      orgName: orgNameProp || base.orgName,
      logoUrl: logoUrlProp || base.logoUrl,
      // Accent facture (montant dû)
      invoiceAccent: warn,
      invoiceAccentSoft: '#fff8eb',
    };
  }, [settings, orgNameProp, logoUrlProp]);

  const labels = useMemo(
    () => ({
      title: t('contributionsPage.invoice.title'),
      org: t('contributionsPage.invoice.org'),
      member: t('contributionsPage.invoice.member'),
      issued: t('contributionsPage.invoice.issued'),
      due: t('contributionsPage.invoice.due'),
      ref: t('contributionsPage.invoice.ref'),
      period: t('contributionsPage.invoice.period'),
      amount: t('contributionsPage.invoice.amountDue'),
      method: t('contributionsPage.invoice.method'),
      status: t('contributionsPage.invoice.status'),
      notes: t('contributionsPage.invoice.notes'),
      total: t('contributionsPage.invoice.totalDue'),
      currency: t('contributionsPage.invoice.currency'),
      footer: t('contributionsPage.invoice.footer'),
    }),
    [t]
  );

  const rows = useMemo(
    () => (Array.isArray(contributions) ? contributions.filter(Boolean) : []),
    [contributions]
  );
  const total = useMemo(
    () => rows.reduce((s, c) => s + (Number(c?.amount) || 0), 0),
    [rows]
  );

  const invoiceNumber = useMemo(() => {
    const ids = rows.map((c) => String(c?.id || '').trim()).filter(Boolean);
    if (!ids.length) return '';
    const stamp = String(issuedAt || '').replace(/\D/g, '').slice(0, 8) || 'AVI';
    if (ids.length === 1) return `AVI-${ids[0].slice(0, 10)}`;
    return `AVI-${stamp}-${ids.length}`;
  }, [rows, issuedAt]);

  const primaryRef = useMemo(() => invoiceNumber || rows.map((c) => c?.id).filter(Boolean).join('|'), [invoiceNumber, rows]);

  useEffect(() => {
    let cancelled = false;
    if (!open || !primaryRef) {
      setQrDataUrl('');
      return undefined;
    }
    (async () => {
      try {
        const url = await QRCode.toDataURL(primaryRef, {
          width: 160,
          margin: 1,
          errorCorrectionLevel: 'M',
          color: { dark: branding.navy || '#16324A', light: '#ffffff' },
        });
        if (!cancelled) setQrDataUrl(url);
      } catch {
        if (!cancelled) setQrDataUrl('');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, primaryRef, branding.navy]);

  const text = useMemo(
    () =>
      buildReceiptLines({
        orgName: branding.orgName,
        memberName,
        contributions: rows,
        issuedAt,
        labels: {
          ...labels,
          title: labels.title,
          amount: labels.amount,
          total: labels.total,
        },
        branding,
      }),
    [branding, memberName, rows, issuedAt, labels]
  );

  if (!open || rows.length === 0) return null;

  const notify = (kind, message) => {
    if (typeof onToast === 'function') onToast(kind, message);
  };

  const pdfFilename = `avis-cotisation-${String(rows[0]?.id || 'avis').slice(0, 12)}.pdf`;
  const imageFilename = `avis-cotisation-${String(rows[0]?.id || 'avis').slice(0, 12)}.png`;

  const buildPdf = async () => {
    if (!previewRef.current) throw new Error('missing_preview');
    return generateReceiptPdfFromElement(previewRef.current, { filename: pdfFilename });
  };

  const buildImage = async () => {
    if (!previewRef.current) throw new Error('missing_preview');
    return generateReceiptImageFromElement(previewRef.current, { filename: imageFilename });
  };

  const handlePrint = () => {
    // Réutilise l'impression navigateur sur le nœud capturé via fenêtre dédiée simple
    const w = window.open('', '_blank', 'noopener,noreferrer,width=720,height=900');
    if (!w || !previewRef.current) {
      notify('error', t('contributionsPage.invoice.printBlocked'));
      return;
    }
    w.document.open();
    w.document.write(`<!DOCTYPE html><html><head><title>${labels.title}</title></head><body>${previewRef.current.outerHTML}</body></html>`);
    w.document.close();
    setTimeout(() => {
      try {
        w.focus();
        w.print();
      } catch {
        // ignore
      }
    }, 200);
  };

  const handleDownload = async () => {
    setBusy('download');
    try {
      const { blob, filename } = await buildPdf();
      downloadBlob(blob, filename);
      notify('success', t('contributionsPage.invoice.pdfDownloaded'));
    } catch {
      notify('error', t('contributionsPage.invoice.pdfFailed'));
    } finally {
      setBusy('');
    }
  };

  const handleCopy = async () => {
    setBusy('copy');
    try {
      await copyReceiptText(text);
      notify('success', t('contributionsPage.invoice.copied'));
    } catch {
      notify('error', t('contributionsPage.invoice.copyFailed'));
    } finally {
      setBusy('');
    }
  };

  const handleShare = async () => {
    setBusy('share');
    try {
      const { file } = await buildImage();
      const mode = await shareReceiptToMember({
        phone: memberPhone,
        title: t('contributionsPage.invoice.title'),
        text: `${branding.orgName} — ${t('contributionsPage.invoice.title')}`,
        file,
        orgName: branding.orgName,
        memberName,
        reference: primaryRef,
      });
      if (mode === 'whatsapp-clipboard') {
        notify('success', t('contributionsPage.invoice.sharedWhatsappPaste'));
      } else if (mode === 'clipboard') {
        notify('success', t('contributionsPage.invoice.imageCopied'));
      } else {
        notify('success', t('contributionsPage.invoice.imageShared'));
      }
    } catch (err) {
      if (err?.name === 'AbortError') return;
      if (err?.code === 'share_unavailable' || err?.message === 'share_unavailable') {
        notify('error', t('contributionsPage.invoice.shareUnavailable'));
        return;
      }
      notify('error', t('contributionsPage.invoice.shareFailed'));
    } finally {
      setBusy('');
    }
  };

  const contactLine = [branding.address, branding.phone, branding.email]
    .filter(Boolean)
    .join(' · ');
  const initial = String(branding.orgName || 'E').trim().slice(0, 1).toUpperCase() || 'E';

  return (
    <ModalShell
      maxWidthClass="max-w-lg"
      zClass="z-[60]"
      onBackdropClick={onClose}
      panelClassName="flex min-h-0 flex-col overflow-hidden p-0"
    >
      <div className="mm-modal-header">
        <div className="min-w-0">
          <p className="mm-hero__label" style={{ color: branding.invoiceAccent }}>
            {t('contributionsPage.invoice.kicker')}
          </p>
          <h3 className="mt-0.5 truncate font-display text-lg font-extrabold tracking-tight text-[#0f3350]">
            {t('contributionsPage.invoice.previewTitle')}
          </h3>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="mm-press flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-100/80 text-slate-500"
          aria-label={t('contributionsPage.invoice.closeAria')}
        >
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto bg-[#f7f4ef] px-3 py-4 sm:px-5">
        <div
          ref={previewRef}
          className="mx-auto max-w-[420px] overflow-hidden rounded-2xl bg-white shadow-[0_12px_40px_rgba(22,50,74,0.14)]"
        >
          <div
            className="relative px-5 pb-5 pt-5 text-white"
            style={{
              background: `linear-gradient(135deg, ${branding.navy} 0%, ${branding.primaryDark} 45%, ${branding.invoiceAccent} 130%)`,
            }}
          >
            <div className="relative flex items-start gap-3.5">
              {branding.logoUrl ? (
                <img
                  src={branding.logoUrl}
                  alt=""
                  className="h-14 w-14 shrink-0 rounded-2xl bg-white object-contain p-1.5 shadow-sm"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                    const fallback = e.currentTarget.nextElementSibling;
                    if (fallback) fallback.classList.remove('hidden');
                  }}
                />
              ) : null}
              <div
                className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-white text-xl font-extrabold shadow-sm ${
                  branding.logoUrl ? 'hidden' : ''
                }`}
                style={{ color: branding.invoiceAccent }}
                aria-hidden
              >
                {initial}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-white/85">
                  {labels.title}
                </p>
                <p className="mt-0.5 text-[15px] font-bold leading-snug break-words">
                  {branding.orgFullName || branding.orgName}
                </p>
                {branding.orgFullName && branding.orgName ? (
                  <p className="mt-0.5 text-xs leading-snug text-white/85 break-words">{branding.orgName}</p>
                ) : null}
              </div>
            </div>
            {contactLine ? (
              <p className="relative mt-3 text-[11px] leading-snug text-white/85">{contactLine}</p>
            ) : null}
          </div>

          <div className="space-y-3 px-5 py-4">
            <div
              className="rounded-xl border px-3.5 py-3"
              style={{ borderColor: branding.line, background: branding.invoiceAccentSoft }}
            >
              <div className="flex items-end justify-between gap-3">
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: branding.muted }}>
                    {labels.total}
                  </p>
                  <p
                    className="mt-1 text-2xl font-extrabold tabular-nums leading-none"
                    style={{ color: branding.invoiceAccent }}
                  >
                    {formatReceiptAmount(total, localeFmt)}
                    <span className="ml-1 text-[11px] font-semibold" style={{ color: branding.muted }}>
                      {labels.currency}
                    </span>
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: branding.muted }}>
                    {t('contributionsPage.invoice.number')}
                  </p>
                  <p className="mt-0.5 max-w-[9rem] break-all text-xs font-semibold" style={{ color: branding.text }}>
                    {invoiceNumber || '—'}
                  </p>
                </div>
              </div>
            </div>

            <div
              className="grid grid-cols-2 gap-3 rounded-xl border px-3.5 py-3"
              style={{ borderColor: branding.line, background: branding.primarySoft }}
            >
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: branding.muted }}>
                  {labels.member}
                </p>
                <p className="mt-0.5 text-sm font-semibold" style={{ color: branding.text }}>
                  {memberName || '—'}
                </p>
              </div>
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: branding.muted }}>
                  {labels.issued}
                </p>
                <p className="mt-0.5 text-sm font-medium" style={{ color: branding.text }}>
                  {issuedAt || '—'}
                </p>
                {dueAt ? (
                  <>
                    <p className="mt-2 text-[10px] font-semibold uppercase tracking-wide" style={{ color: branding.muted }}>
                      {labels.due}
                    </p>
                    <p className="mt-0.5 text-sm font-semibold" style={{ color: branding.invoiceAccent }}>
                      {dueAt}
                    </p>
                  </>
                ) : null}
              </div>
            </div>

            {primaryRef && qrDataUrl ? (
              <div
                className="flex items-center gap-3 rounded-xl border px-3.5 py-3"
                style={{ borderColor: branding.line }}
              >
                <img
                  src={qrDataUrl}
                  alt={t('contributionsPage.invoice.qrAlt')}
                  className="h-[72px] w-[72px] shrink-0 rounded-lg bg-white"
                />
                <div className="min-w-0">
                  <p className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: branding.muted }}>
                    {t('contributionsPage.invoice.qrLabel')}
                  </p>
                  <p className="mt-1 break-all text-[11px] font-medium leading-snug" style={{ color: branding.text }}>
                    {primaryRef}
                  </p>
                </div>
              </div>
            ) : null}

            {rows.map((c, idx) => (
              <article
                key={c.id || `${c.month}-${idx}`}
                className="rounded-xl border px-3.5 py-3"
                style={{ borderColor: branding.line }}
              >
                {rows.length > 1 && (
                  <p className="mb-2 text-[10px] font-bold uppercase tracking-wide" style={{ color: branding.invoiceAccent }}>
                    #{idx + 1}
                  </p>
                )}
                {c.id ? (
                  <p className="mb-2 break-all text-[10px]" style={{ color: branding.muted }}>
                    {labels.ref} {c.id}
                  </p>
                ) : null}
                <div className="flex items-end justify-between gap-3">
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: branding.muted }}>
                      {labels.period}
                    </p>
                    <p className="text-sm font-semibold" style={{ color: branding.text }}>
                      {c.periodLabel || c.month || '—'}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: branding.muted }}>
                      {labels.amount}
                    </p>
                    <p className="text-xl font-extrabold tabular-nums leading-none" style={{ color: branding.invoiceAccent }}>
                      {formatReceiptAmount(c.amount, localeFmt)}
                      <span className="ml-1 text-[10px] font-semibold" style={{ color: branding.muted }}>
                        {labels.currency}
                      </span>
                    </p>
                  </div>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-2 border-t pt-2.5" style={{ borderColor: branding.line }}>
                  <div>
                    <p className="text-[10px] uppercase tracking-wide" style={{ color: branding.muted }}>
                      {labels.status}
                    </p>
                    <p className="text-xs font-medium" style={{ color: branding.text }}>
                      {c.statusLabel || t('contributionsPage.invoice.statusUnpaid')}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-wide" style={{ color: branding.muted }}>
                      {labels.method}
                    </p>
                    <p className="text-xs font-medium" style={{ color: branding.text }}>
                      {c.payment_method || '—'}
                    </p>
                  </div>
                </div>
              </article>
            ))}

            {rows.length > 1 && (
              <p className="text-right text-sm font-extrabold tabular-nums" style={{ color: branding.invoiceAccent }}>
                {labels.total} : {formatReceiptAmount(total, localeFmt)} {labels.currency}
              </p>
            )}

            <div
              className="border-t border-dashed pt-3 text-[11px] leading-snug"
              style={{ borderColor: branding.line, color: branding.muted }}
            >
              {labels.footer}
            </div>
          </div>
        </div>
      </div>

      <div className="mm-modal-footer grid grid-cols-2 gap-2 sm:grid-cols-4">
        <button
          type="button"
          onClick={handleShare}
          disabled={!!busy}
          className="mm-cta disabled:opacity-60"
          style={{ background: branding.invoiceAccent }}
        >
          {busy === 'share' ? '…' : t('contributionsPage.invoice.share')}
        </button>
        <button
          type="button"
          onClick={handlePrint}
          disabled={!!busy}
          className="mm-cta mm-cta--ghost disabled:opacity-60"
        >
          {t('contributionsPage.invoice.print')}
        </button>
        <button
          type="button"
          onClick={handleCopy}
          disabled={!!busy}
          className="mm-cta mm-cta--ghost disabled:opacity-60"
        >
          {busy === 'copy' ? '…' : t('contributionsPage.invoice.copy')}
        </button>
        <button
          type="button"
          onClick={handleDownload}
          disabled={!!busy}
          className="mm-cta mm-cta--ghost disabled:opacity-60"
        >
          {busy === 'download' ? '…' : t('contributionsPage.invoice.downloadPdf')}
        </button>
      </div>
    </ModalShell>
  );
}
